import { getCityName } from './airports';
import { formatDateShort } from './dates';

// IATA airport code → ISO 3166-1 alpha-2 country code
// Compact mapping for crew-relevant airports. Returns undefined if unknown.
const IATA_COUNTRY: Record<string, string> = {
  // UAE
  DXB: 'AE', AUH: 'AE', SHJ: 'AE',
  // South Korea
  ICN: 'KR', GMP: 'KR', PUS: 'KR', CJU: 'KR',
  // Japan
  NRT: 'JP', HND: 'JP', KIX: 'JP', FUK: 'JP', CTS: 'JP', NGO: 'JP',
  // Thailand
  BKK: 'TH', DMK: 'TH', HKT: 'TH', CNX: 'TH',
  // Singapore
  SIN: 'SG',
  // Hong Kong
  HKG: 'HK',
  // China
  PVG: 'CN', PEK: 'CN', PKX: 'CN', CAN: 'CN', CTU: 'CN', SZX: 'CN',
  // India
  DEL: 'IN', BOM: 'IN', BLR: 'IN', MAA: 'IN', CCU: 'IN', HYD: 'IN',
  // UK
  LHR: 'GB', LGW: 'GB', STN: 'GB', MAN: 'GB', EDI: 'GB',
  // US
  JFK: 'US', LAX: 'US', ORD: 'US', SFO: 'US', MIA: 'US', ATL: 'US', DFW: 'US', IAD: 'US', EWR: 'US', BOS: 'US', SEA: 'US', DEN: 'US', IAH: 'US',
  // France
  CDG: 'FR', ORY: 'FR', NCE: 'FR', LYS: 'FR',
  // Germany
  FRA: 'DE', MUC: 'DE', TXL: 'DE', BER: 'DE', HAM: 'DE', DUS: 'DE',
  // Netherlands
  AMS: 'NL',
  // Turkey
  IST: 'TR', SAW: 'TR', AYT: 'TR',
  // Australia
  SYD: 'AU', MEL: 'AU', BNE: 'AU', PER: 'AU',
  // New Zealand
  AKL: 'NZ', CHC: 'NZ',
  // Maldives
  MLE: 'MV',
  // Sri Lanka
  CMB: 'LK',
  // Indonesia
  CGK: 'ID', DPS: 'ID',
  // Malaysia
  KUL: 'MY', PEN: 'MY', LGK: 'MY',
  // Philippines
  MNL: 'PH', CEB: 'PH',
  // Vietnam
  SGN: 'VN', HAN: 'VN',
  // Taiwan
  TPE: 'TW',
  // Italy
  FCO: 'IT', MXP: 'IT', VCE: 'IT',
  // Spain
  MAD: 'ES', BCN: 'ES',
  // Switzerland
  ZRH: 'CH', GVA: 'CH',
  // Qatar
  DOH: 'QA',
  // Bahrain
  BAH: 'BH',
  // Oman
  MCT: 'OM',
  // Saudi Arabia
  RUH: 'SA', JED: 'SA', DMM: 'SA',
  // Egypt
  CAI: 'EG', HRG: 'EG', SSH: 'EG',
  // Kenya
  NBO: 'KE',
  // South Africa
  JNB: 'ZA', CPT: 'ZA',
  // Ethiopia
  ADD: 'ET',
  // Morocco
  CMN: 'MA',
  // Brazil
  GRU: 'BR', GIG: 'BR',
  // Canada
  YYZ: 'CA', YVR: 'CA', YUL: 'CA',
  // Mexico
  MEX: 'MX', CUN: 'MX',
  // Russia
  SVO: 'RU', DME: 'RU', LED: 'RU',
  // Ireland
  DUB: 'IE',
  // Portugal
  LIS: 'PT',
  // Greece
  ATH: 'GR',
  // Austria
  VIE: 'AT',
  // Belgium
  BRU: 'BE',
  // Denmark
  CPH: 'DK',
  // Sweden
  ARN: 'SE',
  // Norway
  OSL: 'NO',
  // Finland
  HEL: 'FI',
  // Poland
  WAW: 'PL',
  // Czech Republic
  PRG: 'CZ',
  // Hungary
  BUD: 'HU',
  // Jordan
  AMM: 'JO',
  // Lebanon
  BEY: 'LB',
  // Israel
  TLV: 'IL',
  // Pakistan
  ISB: 'PK', KHI: 'PK', LHE: 'PK',
  // Bangladesh
  DAC: 'BD',
  // Nepal
  KTM: 'NP',
  // Myanmar
  RGN: 'MM',
  // Cambodia
  PNH: 'KH', REP: 'KH',
  // Mauritius
  MRU: 'MU',
  // Seychelles
  SEZ: 'SC',
  // Tanzania
  DAR: 'TZ', ZNZ: 'TZ',
  // Nigeria
  LOS: 'NG', ABV: 'NG',
  // Ghana
  ACC: 'GH',
  // Kuwait
  KWI: 'KW',
  // Iraq
  BGW: 'IQ', EBL: 'IQ',
};

function getCountryCode(iata: string): string | undefined {
  return IATA_COUNTRY[iata];
}

// Flag emoji from ISO alpha-2 country code (no lookup table — uses regional indicator symbols)
function countryCodeToFlag(iso: string): string {
  return [...iso.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join('');
}

export function generateGroupChatName(
  cityIata: string,
  rangeStart: string,
  rangeEnd: string,
): string {
  const city = getCityName(cityIata);
  const dateRange = `${formatDateShort(rangeStart)}\u2013${formatDateShort(rangeEnd)}`;
  const country = getCountryCode(cityIata);
  const flag = country ? countryCodeToFlag(country) + ' ' : '';
  return `${flag}${city}, ${dateRange}`;
}
